import { Injectable, EventEmitter } from '@angular/core';
import { Observable, BehaviorSubject, from, of, EMPTY } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NotificationModalComponent } from '../components/notification-modal/notification-modal.component';
import { ApiService } from './api.service';
import { UserService } from './user.service';
import { NotificationMessageHelperService } from './notification-helpers/notification-message-helper.service';
import { NotificationItem, NotificationResult } from '../interfaces/notification.interface';
import { ElementPathNode, normalizeElementPath } from '../utils/element-path.utils';
import {
  UserProfileSettings,
  NotificationObject,
  NotificationMessage,
} from './notification-helpers/notification.types';

export interface GetNotificationsOptions {
  projectId?: string | string[];
  category?: string | string[];
  codes?: string[];
  notifyByUsers?: string | string[];
  sections?: string | string[];
  subSections?: string | string[];
  startDate?: number;
  endDate?: number;
  pagingState?: string;
  limit?: number;
  seen?: boolean;
  mentions?: boolean;
}

export const NOTIFICATION_STATUS: { LOADING: string; LOADED: string; ERROR: string } = {
  LOADING: 'LOADING',
  LOADED: 'LOADED',
  ERROR: 'ERROR',
};
const MASTER_AI_EMAIL = 'system-ai-agent@mtm.video';

export const listenerNotificationStatus: EventEmitter<string> = new EventEmitter<string>();
export const contentLanguageUpdated: EventEmitter<string> = new EventEmitter<string>();

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private static readonly ZERO_UUID = '00000000-0000-0000-0000-000000000000';
  private readonly messageSource: BehaviorSubject<string> = new BehaviorSubject('');
  currentMessage: Observable<string> = this.messageSource.asObservable();
  public changeMessage(message: string): void {
    this.messageSource.next(message);
  }

  notificationStatus: string;
  allNotifications: NotificationItem[] = [];
  todayNotifications: NotificationItem[] = [];
  pastNotifications: NotificationItem[] = [];
  notificationUnseenCount: number = 0;
  displayLabelOfUnseenNotificaiton: string = '0';
  private userProfileSettings: UserProfileSettings | undefined;

  constructor(
    private readonly modalService: NgbModal,
    private readonly apiService: ApiService,
    private readonly userService: UserService,
    private readonly notificationMessageHelper: NotificationMessageHelperService
  ) {}

  modalRef: NgbModalRef;
  public open(data: any, avoidDuplicate?: any): Observable<any> {
    if (avoidDuplicate && this.modalRef) {
      this.modalRef.close();
    }

    this.modalRef = this.modalService.open(NotificationModalComponent, {
      size: data.size ?? 'sm',
      backdrop: data.backdrop,
      windowClass: data.windowClass,
    });
    this.modalRef.componentInstance.setParams(data);
    return from(this.modalRef.result).pipe(
      catchError(() => {
        // Modal was dismissed or closed without result
        return of(false);
      })
    );
  }

  public prepareNotification(item: NotificationItem): NotificationItem {
    item.object = item.objectJson ? (JSON.parse(item.objectJson) as NotificationObject) : null;

    if (item.code === 'AI_PROCESSING_MESSAGE') {
      const status = (item.object?.processingStatus ?? '').toUpperCase();
      if (status === 'SUCCESS' || status === 'PROCESSING') {
        item.notificationDisplay = {} as NotificationMessage;
        return item;
      }
    }

    if (item.object?.targetUsername && !item.object.notifiedTargetUsername) {
      item.object.notifiedTargetUsername = item.object.targetUsername;
    }
    if (item.object?.notifiedBy && !item.object.notifiedTargetUsername) {
      item.object.notifiedTargetUsername = item.username;
    }
    if (item.object?.account && !item.object.notifiedBy) {
      const fullName = `${item.object.account.firstName ?? ''} ${item.object.account.lastName ?? ''}`.trim();
      item.object.notifiedBy = fullName || item.object.account.email;
      item.object.notifiedByUsername = item.object.accountEmail ?? item.object.account.email;
    }
    if (item.object?.agentName) {
      item.object.notifiedBy = 'AI';
    }
    if (
      item.object &&
      [item.object.notifiedBy, item.object.notifiedByUsername].some((v) => v?.toLowerCase() === MASTER_AI_EMAIL)
    ) {
      item.object.notifiedBy = 'MASTER AI';
    }
    this.backfillFromElementPath(item);
    item.date = item.time;

    const today: Date = new Date();
    const startOfDay: number = new Date(today.setHours(0, 0, 0, 0)).getTime();
    const endOfDay: number = new Date(today.setHours(23, 59, 59, 999)).getTime();

    item.isToday = item.date >= startOfDay && item.date <= endOfDay;

    item.notificationDisplay = this.getMessage(item.code, item.object ?? null) ?? {};
    return item;
  }

  public addNewNotification(item: NotificationItem, isInit: boolean = false): void {
    this.prepareNotification(item);
    if (isInit) this.allNotifications.push(item);
    else this.allNotifications.unshift(item);

    this.allNotifications = this.allNotifications.filter((item: NotificationItem) => item.notificationDisplay?.label);
    this.todayNotifications = this.allNotifications.filter((k: NotificationItem) => k.isToday);
    this.pastNotifications = this.allNotifications.filter((k: NotificationItem) => !k.isToday);

    if (!isInit) {
      this.setNotificationStatus(NOTIFICATION_STATUS.LOADED);
      this.notificationUnseenCount++;
    }

    if (this.notificationUnseenCount < 9) {
      this.displayLabelOfUnseenNotificaiton = this.notificationUnseenCount.toString();
    } else {
      this.displayLabelOfUnseenNotificaiton = '9+';
    }
  }

  private setNotificationStatus(status: string): void {
    this.notificationStatus = status;
    listenerNotificationStatus.emit(status);
  }

  private clearNotificationVariables(): void {
    this.allNotifications = [];
    this.todayNotifications = [];
    this.pastNotifications = [];
    this.notificationUnseenCount = 0;

    this.setNotificationStatus(NOTIFICATION_STATUS.LOADING);
  }

  public getAllUnseenNotifications(): void {
    this.clearNotificationVariables();

    this.userService
      .getUserProfileSettings()
      .pipe(
        switchMap((result: UserProfileSettings) => {
          this.userProfileSettings = result;
          return this.getAllUnseenNotificationsFromAPI();
        })
      )
      .subscribe({
        next: (data: { notifications?: NotificationItem[]; unseenCount?: number }) => {
          if (!Array.isArray(data?.notifications)) {
            this.setNotificationStatus(NOTIFICATION_STATUS.ERROR);
            return;
          }

          this.clearNotificationVariables();
          this.notificationUnseenCount = data.unseenCount ?? 0;
          data.notifications.forEach((item: NotificationItem) => this.addNewNotification(item, true));
          this.setNotificationStatus(NOTIFICATION_STATUS.LOADED);
        },
        error: () => this.setNotificationStatus(NOTIFICATION_STATUS.ERROR),
      });
  }

  /**
   * With `projectId`, counts cover that project plus the no-project (zero-UUID) scope,
   * matching a list fetched with `projectIds: [projectId, EMPTY_UUID]`. Without it, counts are global.
   */
  public getAllUnseenNotificationsFromAPI(projectId?: string): Observable<any> {
    const query: string = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
    return this.apiService.httpGet(`/api/notifications/unseen${query}`);
  }

  public getUnseenProjectNotificationsCount(projectId: string | string[] | null | undefined): Observable<any> {
    if (Array.isArray(projectId)) {
      projectId = projectId.filter(Boolean);
    }
    if (Array.isArray(projectId) && projectId.length > 1) {
      return this.apiService.httpPost(`/api/notifications/project/unseen`, projectId);
    } else if (projectId?.length) {
      return this.apiService.httpGet(`/api/notifications/project/${projectId}/unseen`);
    } else {
      return EMPTY;
    }
  }

  public getUnseenWorkspaceNotificationsCount(workspaceId: string): Observable<any> {
    return this.apiService.httpGet(`/api/notifications/workspace/${workspaceId}/unseen`);
  }

  /*
	Mark all notifications as seen. If project id is supplied then only notifications in that project are affected
	 */
  public markNotificationsAsSeen(projectId: string | string[] | undefined | null): Observable<any> {
    let validProjectId: string | undefined;
    if (Array.isArray(projectId)) {
      const filtered = projectId.filter(Boolean);
      validProjectId = filtered.length > 0 ? (typeof filtered[0] === 'string' ? filtered[0] : undefined) : undefined;
    } else {
      validProjectId = projectId && typeof projectId === 'string' && projectId.trim() !== '' ? projectId : undefined;
    }

    const endpoint: string = !validProjectId ? `/api/notifications` : `/api/notifications/${validProjectId}/unseen`;
    return this.apiService.httpPut(endpoint, {});
  }

  public getNotifications(options: GetNotificationsOptions = {}): Observable<NotificationResult> {
    const {
      projectId,
      category,
      codes,
      notifyByUsers,
      sections,
      subSections,
      startDate,
      endDate,
      pagingState,
      limit = 25,
      seen,
      mentions,
    } = options;

    return this.userService.getUserProfileSettings().pipe(
      switchMap((result: UserProfileSettings) => {
        this.userProfileSettings = result;
        let filteredProjectId: string | string[] | undefined = projectId;
        let filteredCategory: string | string[] | undefined = category;
        if (Array.isArray(projectId)) {
          filteredProjectId = projectId.filter(Boolean);
        }
        if (Array.isArray(category)) {
          filteredCategory = category.filter(Boolean);
        }
        let projectIdsArray: string[] | undefined;
        if (filteredProjectId && (Array.isArray(filteredProjectId) && filteredProjectId.length > 0)) {
          projectIdsArray = Array.isArray(filteredProjectId) ? filteredProjectId : [filteredProjectId];
        }
        const payload: {
          projectIds?: string[];
          category?: string | string[];
          codes?: string[];
          notifyByUsers?: string | string[];
          sections?: string | string[];
          subSections?: string | string[];
          startDate?: number;
          endDate?: number;
          pagingState?: string;
          limit: number;
          seen?: boolean;
          mentions?: boolean;
        } = {
          projectIds: projectIdsArray,
          category: filteredCategory || undefined,
          codes: codes || undefined,
          notifyByUsers: notifyByUsers || undefined,
          sections: sections || undefined,
          subSections: subSections || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          pagingState: pagingState || undefined,
          limit: limit,
          // `seen: false` is a real filter, so these must not use the `|| undefined` pattern above
          seen,
          mentions,
        };
        Object.keys(payload).forEach((key: string) => {
          if (payload[key as keyof typeof payload] === undefined) delete payload[key as keyof typeof payload];
        });
        return this.apiService.httpPost('/api/notifications', payload);
      }),
      map((result: NotificationResult) => {
        result.data.forEach((item: NotificationItem) => {
          this.prepareNotification(item);
        });
        return result;
      })
    );
  }

  public getStages(): Observable<any> {
    return this.apiService.httpGet('/api/notifications/stages');
  }

  private backfillFromElementPath(item: NotificationItem): void {
    if (!item.object) {
      return;
    }

    this.backfillFromElementUuid(item);

    if (!item.object.elementPath) {
      this.normalizeVersionNumber(item);
      return;
    }

    const path: Record<string, ElementPathNode> = normalizeElementPath(item.object.elementPath);

    const node = (key: string): ElementPathNode | undefined => {
      const n = path[key];
      return n?.id ? n : undefined;
    };

    if (!item.object.subSectionName) {
      item.object.subSectionName = node('SUB-SECTION')?.name;
    }
    if (!item.object.itemName) {
      item.object.itemName = node('SUB-FOLDER')?.name;
    }
    if (!item.object.versionNumber) {
      item.object.versionNumber = node('VERSION')?.name;
    }
    if (!item.object.sectionName) {
      item.object.sectionName = node('SECTION')?.name;
    }
    if (!item.object.projectName) {
      item.object.projectName = node('PROJECT')?.name;
    }

    if (!item.object.sectionId) {
      item.object.sectionId = node('SECTION')?.id;
    }
    if (!item.object.subSectionId) {
      item.object.subSectionId = node('SUB-SECTION')?.id;
    }

    if (!item.object.workspaceId) {
      item.object.workspaceId = node('WORKSPACE')?.id;
    }
    if (!item.object.campaignId) {
      item.object.campaignId = node('CAMPAIGN')?.id;
    }
    if (!item.object.optionId) {
      item.object.optionId = node('SUB-FOLDER')?.id;
    }
    if (!item.object.fileId) {
      item.object.fileId = node('FILE')?.id;
    }

    const projectNode = node('PROJECT');
    if (projectNode?.id && item.projectId === NotificationService.ZERO_UUID) {
      item.projectId = projectNode.id;
      item.object.projectId = projectNode.id;
    }

    if (!item.object.elementName && item.object.elementProperties?.name) {
      item.object.elementName = item.object.elementProperties.name;
    }

    this.normalizeVersionNumber(item);
  }

  private backfillFromElementUuid(item: NotificationItem): void {
    const elementType = item.object?.elementType;
    const elementUuid = item.object?.elementUuid;
    if (!elementType || !elementUuid) {
      return;
    }

    if (elementType === 'WORKSPACE' && !item.object.workspaceId) {
      item.object.workspaceId = elementUuid;
    }
    if (elementType === 'CAMPAIGN' && !item.object.campaignId) {
      item.object.campaignId = elementUuid;
    }
    if (elementType === 'PROJECT' && item.projectId === NotificationService.ZERO_UUID) {
      item.projectId = elementUuid;
      item.object.projectId = elementUuid;
    }
    if (elementType === 'SECTION' && !item.object.sectionId) {
      item.object.sectionId = elementUuid;
    }
    if (elementType === 'SUB-SECTION' && !item.object.subSectionId) {
      item.object.subSectionId = elementUuid;
    }
    if (elementType === 'FOLDER' && !item.object.topicId) {
      item.object.topicId = elementUuid;
    }
    if (elementType === 'SUB-FOLDER' && !item.object.optionId) {
      item.object.optionId = elementUuid;
    }
    if (elementType === 'FILE' && !item.object.fileId) {
      item.object.fileId = elementUuid;
    }
  }

  private normalizeVersionNumber(item: NotificationItem): void {
    if (!item.object?.versionNumber) {
      return;
    }
    const match = item.object.versionNumber.match(/^Version\s+(\d+)$/i);
    if (match) {
      item.object.versionNumber = match[1];
    }
  }

  /**
   * Get message for display notification
   * @param code - Notification code
   * @param object - Notification object with data
   */
  public getMessage(code: string, object: NotificationObject | null = null): NotificationMessage | null {
    return this.notificationMessageHelper.getMessage(code, object, this.userProfileSettings);
  }
}
